import { useEffect, useRef } from 'react';
import { initNative } from './lib/native';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './lib/auth';
import type { AuthResponse } from './lib/auth';
import { useContractorAuthStore } from './lib/contractorAuth';
import type { ContractorAuthResponse } from './lib/contractorAuth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ContractorProtectedRoute } from './components/ContractorProtectedRoute';
import { SideNav }              from './components/SideNav';
import { NavBar }               from './components/NavBar';
import { LoginPage }            from './pages/LoginPage';
import { RegisterPage }         from './pages/RegisterPage';
import { TwoFactorLogin }       from './pages/auth/TwoFactorLogin';
import { SecuritySettings }     from './pages/settings/Security';
import { SettingsPage }         from './pages/settings/Settings';
import { HomeSetupPage }        from './pages/HomeSetupPage';
import { DashboardPage }        from './pages/DashboardPage';
import { TasksPage }            from './pages/TasksPage';
import { HomeProfile }          from './pages/profile/HomeProfile';
import { EditHome }             from './pages/profile/EditHome';
import { ApplianceRegistry }    from './pages/profile/ApplianceRegistry';
import { SolarDetail }          from './pages/profile/SolarDetail';
import { PaintColors }          from './pages/profile/PaintColors';
import { Insights }                  from './pages/insights/Insights';
import { DocumentVault }             from './pages/vault/DocumentVault';
import { LeaveReview }               from './pages/reviews/LeaveReview';
import { ReviewConfirmation }        from './pages/reviews/ReviewConfirmation';
import { ContractorPublicProfile }   from './pages/reviews/ContractorPublicProfile';
import { RespondToReview }           from './pages/reviews/RespondToReview';
import { ContractorDashboard }       from './pages/contractor/ContractorDashboard';
import { LeadInbox }                 from './pages/contractor/LeadInbox';
import { ContractorAnalytics }       from './pages/contractor/ContractorAnalytics';
import { WebhookManager }            from './pages/contractor/WebhookManager';
import { ContractorSignup }          from './pages/contractor/ContractorSignup';
import { ContractorLogin }           from './pages/contractor/ContractorLogin';
import { ServiceArea }               from './pages/contractor/ServiceArea';
import { LicenseCoverageMap }        from './pages/admin/LicenseCoverageMap';
import { JobScheduler }             from './pages/contractor/JobScheduler';
import { TeamManagement }           from './pages/contractor/TeamManagement';
import { JobMessaging as ContractorJobMessaging } from './pages/contractor/JobMessaging';
import { JobMessaging as HomeownerJobMessaging }  from './pages/JobMessaging';
import { Availability }             from './pages/contractor/Availability';
import { MaintenanceReport }         from './pages/vault/MaintenanceReport';
import { YearlyCalendar }            from './pages/YearlyCalendar';
import { MaintenanceHistory }        from './pages/MaintenanceHistory';
import { SafetyHub }                 from './pages/safety/SafetyHub';
import { SmokeAlarms }              from './pages/safety/SmokeAlarms';
import { AddSmokeAlarm }            from './pages/safety/AddSmokeAlarm';
import { FireExtinguishers }         from './pages/safety/FireExtinguishers';
import { AddFireExtinguisher }       from './pages/safety/AddFireExtinguisher';
import { TrashReminder }             from './pages/safety/TrashReminder';
import { RoofProfile }               from './pages/safety/RoofProfile';
import { VerifyEmailPage }          from './pages/VerifyEmailPage';
import { VerifyPendingPage }        from './pages/VerifyPendingPage';
import { EarlyAccessPage }          from './pages/EarlyAccessPage';
import { PrivacyPolicy }            from './pages/legal/PrivacyPolicy';
import { TermsOfService }           from './pages/legal/TermsOfService';
import { EarlyAccessDisclaimer }    from './pages/legal/EarlyAccessDisclaimer';
import { API_BASE } from './lib/apiBase';

function useNativeInit() {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    initNative().catch(() => { /* non-fatal */ });
  }, []);
}

function useAuthInit() {
  const { setAuth, setInitialized } = useAuthStore();

  useEffect(() => {
    fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(res => (res.ok ? res.json() as Promise<AuthResponse> : Promise.resolve(null)))
      .then(data => { if (data) setAuth(data.user, data.access_token, data.expires_in); })
      .catch(() => { /* not logged in */ })
      .finally(() => {
        setInitialized();
        window.__dwelfixHideSplash?.();
      });
  }, [setAuth, setInitialized]);
}

function useContractorAuthInit() {
  const { setAuth, setInitialized } = useContractorAuthStore();

  useEffect(() => {
    // Only attempt refresh when a contractor session hint exists in localStorage.
    // Homeowners visiting the app should never trigger the contractor/refresh call.
    let hasHint = false;
    try { hasHint = !!localStorage.getItem('contractor_session'); } catch { hasHint = true; }
    if (!hasHint) { setInitialized(); return; }

    fetch(`${API_BASE}/contractor/refresh`, { method: 'POST', credentials: 'include' })
      .then(res => (res.ok ? res.json() as Promise<ContractorAuthResponse> : Promise.resolve(null)))
      .then(data => { if (data) setAuth(data.contractor, data.access_token, data.expires_in); })
      .catch(() => { /* not logged in as contractor */ })
      .finally(() => setInitialized());
  }, [setAuth, setInitialized]);
}

// Resume handler: re-validate auth when app returns from background
function useAppResume() {
  const { accessToken, setAuth, logout } = useAuthStore();
  useEffect(() => {
    function onResume() {
      if (!accessToken) return;
      fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
        .then(res => (res.ok ? res.json() as Promise<AuthResponse> : null))
        .then(data => { if (data) setAuth(data.user, data.access_token, data.expires_in); })
        .catch(() => { logout(); });
    }
    window.addEventListener('dwelfix:app-resume', onResume);
    return () => window.removeEventListener('dwelfix:app-resume', onResume);
  }, [accessToken, setAuth, logout]);
}

function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function ContractorProtected({ children }: { children: React.ReactNode }) {
  return <ContractorProtectedRoute>{children}</ContractorProtectedRoute>;
}

// Routes that show the app sidebar/bottom nav
const APP_ROUTE_PREFIXES = ['/app/'];

// Show SideNav and NavBar only on app routes, not auth/marketing pages
function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isAppRoute = APP_ROUTE_PREFIXES.some(p => location.pathname.startsWith(p));
  const user = useAuthStore((s) => s.user);
  const contractor = useContractorAuthStore((s) => s.contractor);
  const showNav = isAppRoute && (!!user || !!contractor);

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 768px) {
          .app-nav-mobile { display: none !important; }
          .app-nav-desktop { display: flex !important; }
          .app-content { padding-left: 240px; }
        }
        @media (max-width: 767px) {
          .app-nav-desktop { display: none !important; }
          .app-nav-mobile { display: flex !important; }
        }
      `}</style>
      {showNav && (
        <div className="app-nav-desktop" style={{ display: 'none' }}>
          <SideNav />
        </div>
      )}
      <div className={showNav ? 'app-content' : ''}>
        {children}
      </div>
      {showNav && (
        <div className="app-nav-mobile" style={{ display: 'none' }}>
          <NavBar />
        </div>
      )}
    </>
  );
}

// Root handler: logged-in users go to /app/dashboard; logged-out users go to
// /login. Fresh browser visits to / are served index-marketing.html by the
// CDN before React ever loads, so this only fires on in-app client-side nav.
function RootRedirect() {
  const initialized = useAuthStore((s) => s.initialized);
  const user = useAuthStore((s) => s.user);
  if (!initialized) return null;
  return <Navigate to={user ? '/app/dashboard' : '/login'} replace />;
}

export default function App() {
  useNativeInit();
  useAuthInit();
  useContractorAuthInit();
  useAppResume();

  return (
    <AppShell>
      <Routes>
        {/* Root */}
        <Route path="/" element={<RootRedirect />} />

        {/* Homeowner auth */}
        <Route path="/login"          element={<LoginPage />} />
        <Route path="/register"       element={<RegisterPage />} />
        <Route path="/app/2fa-verify" element={<TwoFactorLogin />} />

        {/* Contractor auth */}
        <Route path="/contractor/login"  element={<ContractorLogin />} />
        <Route path="/contractor/signup" element={<ContractorSignup />} />

        {/* Public / semi-auth */}
        <Route path="/verify-pending"  element={<VerifyPendingPage />} />
        <Route path="/verify-email"    element={<VerifyEmailPage />} />
        <Route path="/early-access"    element={<EarlyAccessPage />} />
        <Route path="/privacy"         element={<PrivacyPolicy />} />
        <Route path="/terms"           element={<TermsOfService />} />
        <Route path="/disclaimer"      element={<EarlyAccessDisclaimer />} />
        <Route path="/contractors/:id" element={<ContractorPublicProfile />} />

        {/* ── App routes — all under /app/* ── */}
        <Route path="/app/home/setup" element={<Protected><HomeSetupPage /></Protected>} />

        {/* Core homeowner */}
        <Route path="/app/dashboard" element={<Protected><DashboardPage /></Protected>} />
        <Route path="/app/tasks"     element={<Protected><TasksPage /></Protected>} />

        {/* Profile */}
        <Route path="/app/profile"       element={<Protected><HomeProfile /></Protected>} />
        <Route path="/app/profile/edit"  element={<Protected><EditHome /></Protected>} />
        <Route path="/app/appliances"    element={<Protected><ApplianceRegistry /></Protected>} />
        <Route path="/app/solar"         element={<Protected><SolarDetail /></Protected>} />
        <Route path="/app/paint-colors"  element={<Protected><PaintColors /></Protected>} />

        {/* Insights + Vault */}
        <Route path="/app/insights" element={<Protected><Insights /></Protected>} />
        <Route path="/app/vault"    element={<Protected><DocumentVault /></Protected>} />
        <Route path="/app/reports"  element={<Protected><MaintenanceReport /></Protected>} />

        {/* Calendar + History */}
        <Route path="/app/calendar" element={<Protected><YearlyCalendar /></Protected>} />
        <Route path="/app/history"  element={<Protected><MaintenanceHistory /></Protected>} />

        {/* Safety */}
        <Route path="/app/safety"                   element={<Protected><SafetyHub /></Protected>} />
        <Route path="/app/smoke-alarms"             element={<Protected><SmokeAlarms /></Protected>} />
        <Route path="/app/smoke-alarms/add"         element={<Protected><AddSmokeAlarm /></Protected>} />
        <Route path="/app/fire-extinguishers"       element={<Protected><FireExtinguishers /></Protected>} />
        <Route path="/app/fire-extinguishers/add"   element={<Protected><AddFireExtinguisher /></Protected>} />
        <Route path="/app/trash-schedule"           element={<Protected><TrashReminder /></Protected>} />
        <Route path="/app/roof"                     element={<Protected><RoofProfile /></Protected>} />

        {/* Reviews */}
        <Route path="/app/reviews/leave/:jobId"       element={<Protected><LeaveReview /></Protected>} />
        <Route path="/app/reviews/confirmation"       element={<Protected><ReviewConfirmation /></Protected>} />
        <Route path="/app/reviews/:reviewId/respond"  element={<Protected><RespondToReview /></Protected>} />

        {/* Settings — accessible by both homeowners and contractors */}
        <Route path="/app/settings"          element={<Protected><SettingsPage /></Protected>} />
        <Route path="/app/settings/security" element={<Protected><SecuritySettings /></Protected>} />

        {/* Contractor portal — protected by contractor auth */}
        <Route path="/app/contractor/dashboard"    element={<ContractorProtected><ContractorDashboard /></ContractorProtected>} />
        <Route path="/app/contractor/leads"        element={<ContractorProtected><LeadInbox /></ContractorProtected>} />
        <Route path="/app/contractor/analytics"    element={<ContractorProtected><ContractorAnalytics /></ContractorProtected>} />
        <Route path="/app/contractor/webhooks"     element={<ContractorProtected><WebhookManager /></ContractorProtected>} />
        <Route path="/app/contractor/service-area"  element={<ContractorProtected><ServiceArea /></ContractorProtected>} />
        <Route path="/app/contractor/schedule/:jobId"       element={<ContractorProtected><JobScheduler /></ContractorProtected>} />
        <Route path="/app/contractor/team"                  element={<ContractorProtected><TeamManagement /></ContractorProtected>} />
        <Route path="/app/contractor/jobs/:jobId/messages"  element={<ContractorProtected><ContractorJobMessaging /></ContractorProtected>} />
        <Route path="/app/contractor/availability"          element={<ContractorProtected><Availability /></ContractorProtected>} />

        {/* Homeowner job messaging */}
        <Route path="/app/jobs/:jobId/messages" element={<ProtectedRoute><HomeownerJobMessaging /></ProtectedRoute>} />

        {/* Admin */}
        <Route path="/admin/license-coverage" element={<LicenseCoverageMap />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AppShell>
  );
}
